import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import useHttp from "../../Hooks/use-http";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import UpdateVerificationDocuments from "./UpdateVerificationDocument";
import { FormatDateTime } from "../CommonComponent/DateFormat";
import { ReactComponent as AddSign } from "../../Assets/Plus_sign.svg";

const VerificationDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailID, setEmailID] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedDocID, setSelectedDocID] = useState(null);
  const [isRefresh, setIsRefresh] = useState(false);
  const [mergedDocuments, setMergedDocuments] = useState([]);
  const [copiedItemId, setCopiedItemId] = useState(null);

  const { sendRequest } = useHttp();
  const location = useLocation();

  const documentDetails = useCallback((data) => {
    if (data?.mergedData) {
      setMergedDocuments(data.mergedData);
    }
  }, []);

  useEffect(() => {
    if (location.state?.data) {
      const email =
        typeof location.state.data === "object"
          ? location.state.data.emailID
          : location.state.data;
      if (email) {
        setEmailID(email);
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (!emailID) {
      return;
    }

    sendRequest(
      {
        url: "/documentMergedData",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"),
          data: {
            category: "Driver",
            emailID,
          },
        },
      },
      documentDetails
    );
  }, [emailID, isRefresh, sendRequest, documentDetails]);

  const handleVerify = async (docId, index) => {
    try {
      const approvedBy = localStorage.getItem("user");
      const approvedOn = new Date(
        new Date().getTime() + 5 * 60 * 60 * 1000 + 30 * 60 * 1000
      ).toISOString();

      const currentDoc = mergedDocuments[index];

      // Prepare the update data
      const updateData = {
        id: currentDoc.id,
        documentID: currentDoc.documentID,
        documentNumber: currentDoc.documentNumber,
        url: currentDoc.url,
        isVerified: 1,
        approvedBy: approvedBy,
        approvedOn: approvedOn,
        emailID: emailID || localStorage.getItem("user"),
        category: currentDoc.category,
        ...(currentDoc.expiryDate && { expiryDate: currentDoc.expiryDate }),
      };

      // Update UI immediately
      const updatedDocuments = mergedDocuments.map((doc) =>
        doc.id === currentDoc.id
          ? {
              ...doc,
              isVerified: 1,
              approvedBy,
              approvedOn,
              updatedAt: approvedOn,
            }
          : doc
      );
      setMergedDocuments(updatedDocuments);

      // Send to backend
      const response = await sendRequest({
        url: "/addupdatedocument",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: approvedBy,
          data: [updateData],
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Document verified successfully!");
        setIsRefresh((prev) => !prev);
      } else {
        throw new Error(response.message || "Failed to verify document");
      }
    } catch (error) {
      toast.error(error.message);
      setIsRefresh((prev) => !prev);
    }
  };

  const handleUpdateSuccess = () => {
    setIsRefresh((prev) => !prev);
  };

  const handleCopyClick = (event, documentId) => {
    event.stopPropagation();
    navigator.clipboard.writeText(documentId);
    setCopiedItemId(documentId);
    setTimeout(() => {
      setCopiedItemId(null);
    }, 2000);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h5" sx={{ marginBottom: 2, fontFamily: "Montserrat" }}>
        Verification Documents
      </Typography>
      
      <TableContainer component={Paper} sx={{ width: "100%" }}>
        <Table>
        <TableHead>
          <TableRow>
            <TableCell>Document ID</TableCell>
            <TableCell>Document Number</TableCell>
            <TableCell>Document Name</TableCell>
            <TableCell>Image</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Updated At</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {!mergedDocuments?.length ? (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="h6" align="center">
                  No Documents
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            mergedDocuments.map((doc, index) => {
              const isComplete = doc.url && doc.documentNumber;
              const isVerified = doc?.isVerified === 1 || doc?.isVerified === true;

              return (
                <TableRow key={doc.documentID}>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip
                        title={
                          copiedItemId === doc.documentID
                            ? "Copied!"
                            : "Copy Document ID"
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleCopyClick(event, doc.documentID)
                          }
                          sx={{ 
                            padding: "2px",
                            "& .MuiSvgIcon-root": {
                              fontSize: "0.875rem",
                              color: "#9e9e9e",
                            },
                            "&:hover .MuiSvgIcon-root": {
                              color: "#757575",
                            },
                          }}
                        >
                          <FileCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <span>{doc.documentID}</span>
                    </Box>
                  </TableCell>
                  <TableCell>{doc.documentNumber || "Not provided"}</TableCell>
                  <TableCell>{doc.documentName}</TableCell>

                  <TableCell>
                    {doc.url ? (
                      <div
                        style={{ cursor: "pointer", color: "blue" }}
                        onClick={() => window.open(doc.url, "_blank")}
                      >
                        View
                      </div>
                    ) : (
                      "No File"
                    )}
                  </TableCell>

                  <TableCell>
                    {isComplete
                      ? isVerified
                        ? "Verified"
                        : "Pending"
                      : "Incomplete"}
                  </TableCell>
                  <TableCell>{FormatDateTime(doc.updatedAt)}</TableCell>

                  <TableCell>
                    {isComplete ? (
                      <div style={{ display: "flex", gap: "5px" }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={isVerified}
                          onClick={() => handleVerify(doc.documentID, index)}
                          style={{ marginRight: "8px" }}
                        >
                          {isVerified ? "Verified" : "Verify"}
                        </Button>

                        <IconButton
                          onClick={() => {
                            setSelectedDocID(doc.documentID);
                            setIsUpdateModalOpen(true);
                          }}
                        >
                          <AddSign />
                        </IconButton>
                      </div>
                    ) : (
                      <IconButton
                        onClick={() => {
                          setSelectedDocID(doc.documentID);
                          setIsUpdateModalOpen(true);
                        }}
                      >
                        <AddSign />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
        </Table>
      </TableContainer>

      {isUpdateModalOpen && (
        <UpdateVerificationDocuments
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          emailID={emailID}
          documentss={documents}
          documentID={selectedDocID}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
};

export default VerificationDocuments;
